import React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { CssBaseline, Box, useMediaQuery, Typography, Menu } from '@mui/material';
import MuiAppBar from '@mui/material/AppBar';
import LeftDrawer from './LeftDrawer';
import DrawerHeader from './DrawerHeader';
import { Outlet } from 'react-router-dom';

import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';

import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import NotiContainer from '../../../containers/common/header/NotiContainer';
import UserMenuContainer from '../../../containers/common/header/UserMenuContainer';
import LinkBoxContainer from '../../../containers/common/header/LinkBoxContainer';
import ProjectCounterContainer from '../../../containers/common/projectCounter/ProjectCounterContainer';
import TodayBriefingContainer from '../../../containers/common/briefing/TodayBriefingContainer';
import {
  MobilePageHeaderProvider,
  useMobilePageHeaderContext,
} from '../../../hooks/useMobilePageHeader';

const drawerWidth = 240;

const Main = styled('main', {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isMobile',
})(
  ({ theme, open, isMobile }) => ({
    flexGrow: 1,
    minWidth: 0,
    height: '100dvh',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: isMobile ? theme.spacing(1.5) : theme.spacing(3),
    boxSizing: 'border-box',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: isMobile ? 0 : `-${drawerWidth}px`,
    ...(open && !isMobile && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  }),
);

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isMobile',
})(({ theme, open, isMobile }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && !isMobile && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerTemplate = (props) => (
  <MobilePageHeaderProvider>
    <DrawerTemplateInner {...props} />
  </MobilePageHeaderProvider>
);

const DrawerTemplateInner = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // 상단 앱바 축약(제목 합치기)은 TitleBox/ChanceInfo 등이 쓰는 것과 같은 xs 기준으로
  // 맞춘다. isMobile(<900px, 드로어용)과 섞으면 600~900px 구간에서 축약 앱바와
  // 페이지 자체 헤더(TitleBox 등은 sm+에서 자기 헤더를 그림)가 동시에 나오는 문제가 생긴다.
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const { headerConfig, scrollTargetRef, scrollContentToTop } = useMobilePageHeaderContext();
  const [open, setOpen] = React.useState(!isMobile);

  // 캡션(경로/단계) 한 줄은 스크롤 다운이면 숨기고, 스크롤 업이거나 맨 위면 보인다.
  const [captionVisible, setCaptionVisible] = React.useState(true);
  const lastScrollTopRef = React.useRef(0);
  const [moreAnchorEl, setMoreAnchorEl] = React.useState(null);

  // 축약 앱바는 캡션 유무/표시 여부에 따라 높이가 48~76px 사이로 바뀐다.
  // 아래 본문 스크롤 영역이 고정(position:fixed) 앱바에 가리지 않도록
  // 실제 렌더된 높이를 측정해 스페이서에 반영한다.
  const appBarRef = React.useRef(null);
  const [compactAppBarHeight, setCompactAppBarHeight] = React.useState(48);
  const compactMode = isXs && Boolean(headerConfig);

  React.useEffect(() => {
    if (!compactMode || typeof ResizeObserver === 'undefined') return undefined;
    const el = appBarRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect?.height;
      if (h) setCompactAppBarHeight(Math.ceil(h));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [compactMode]);

  // 다른 화면으로 이동해 제목이 바뀌면 캡션을 다시 보이게 한다.
  // (등록 객체 자체는 매 렌더 갱신되므로 제목 텍스트만 의존성으로 본다.)
  React.useEffect(() => {
    setCaptionVisible(true);
    lastScrollTopRef.current = scrollTargetRef.current?.scrollTop || 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerConfig?.title]);

  const handleContentScroll = (e) => {
    const top = e.currentTarget.scrollTop;
    const last = lastScrollTopRef.current;
    if (top <= 8) {
      setCaptionVisible(true);
    } else if (top > last + 4) {
      setCaptionVisible(false);
    } else if (top < last - 4) {
      setCaptionVisible(true);
    }
    lastScrollTopRef.current = top;
  };

  React.useEffect(() => {
    setOpen(!isMobile);
  }, [isMobile]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  // 메뉴 검색 단축키(Ctrl+K / macOS ⌘K) 는 어디서나 눌릴 수 있어야 한다.
  // MenuList 는 모바일에서 드로어가 닫혀 있으면 DOM 에 아예 없으므로(아래 조건부 렌더),
  // 리스너는 항상 떠 있는 이 컴포넌트가 들고 있다가: (1) 드로어를 열고 (2) 토큰을 올려
  // MenuList 쪽에 "포커스 해라" 신호만 내려보낸다(모바일이면 그 사이에 새로 마운트된다).
  const [focusSearchToken, setFocusSearchToken] = React.useState(0);

  React.useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      const key = (e.key || '').toLowerCase();
      if (key === 'k' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        setOpen(true);
        setFocusSearchToken((t) => t + 1);
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
      <CssBaseline />

      <AppBar ref={appBarRef} position="fixed" open={open} isMobile={isMobile} color="default">
        {compactMode ? (
          <>
            <Toolbar variant="dense" sx={{ minHeight: 48, px: 1, gap: 0.5 }}>
              <IconButton
                color="inherit"
                size="small"
                edge="start"
                aria-label={headerConfig.onBack ? '뒤로가기' : '메뉴 열기'}
                onClick={headerConfig.onBack || handleDrawerOpen}
              >
                {headerConfig.onBack ? (
                  <ArrowBackIcon fontSize="small" />
                ) : (
                  <MenuOpenIcon fontSize="small" />
                )}
              </IconButton>

              <Typography
                variant="subtitle1"
                component="div"
                noWrap
                sx={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: '1rem' }}
              >
                {headerConfig.title}
              </Typography>

              <NotiContainer compact />

              {headerConfig.overflow && (
                <>
                  <IconButton
                    size="small"
                    aria-label="추가 메뉴 열기"
                    onClick={(e) => setMoreAnchorEl(e.currentTarget)}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                  <Menu
                    anchorEl={moreAnchorEl}
                    open={Boolean(moreAnchorEl)}
                    onClose={() => setMoreAnchorEl(null)}
                    onClick={() => setMoreAnchorEl(null)}
                  >
                    {headerConfig.overflow}
                  </Menu>
                </>
              )}

              <UserMenuContainer compact />
            </Toolbar>

            {headerConfig.caption && (
              <Box
                onClick={scrollContentToTop}
                sx={{
                  overflow: 'hidden',
                  maxHeight: captionVisible ? 28 : 0,
                  opacity: captionVisible ? 1 : 0,
                  transition: 'max-height 0.18s ease, opacity 0.12s ease',
                  px: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                  cursor: 'pointer',
                }}
              >
                {headerConfig.caption}
              </Box>
            )}
          </>
        ) : (
          <Toolbar
            variant="dense"
            sx={{
              minHeight: { xs: 48, sm: 56 },
              px: { xs: 1, sm: 2 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
              }}
            >
              <Box>
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  onClick={handleDrawerOpen}
                  edge="start"
                  sx={{
                    mr: { xs: 0.5, md: 2 },
                    ...(open && !isMobile && { display: 'none' }),
                  }}
                >
                  <MenuOpenIcon />
                </IconButton>
              </Box>
            </Box>
            <Box sx={{ mx: { xs: 0, md: 1 } }}>
              <NotiContainer />
            </Box>
            <Box sx={{ mx: { xs: 0, md: 1 } }}>
              <TodayBriefingContainer compact={isMobile} />
            </Box>
            <Box>
              <ProjectCounterContainer compact={isMobile} />
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0.5, md: 1 },
                pl: { xs: 0, md: 1 },
                py: { xs: 0, md: 1 },
                '& > *': {
                  marginRight: { md: 1 },
                },
              }}
            >
              <UserMenuContainer compact={isMobile} />
              {!isMobile && <LinkBoxContainer />}
            </Box>
          </Toolbar>
        )}
      </AppBar>

      {(!isMobile || open) && (
        <LeftDrawer
          theme={theme}
          drawerWidth={drawerWidth}
          open={open}
          handleDrawerClose={handleDrawerClose}
          variant={isMobile ? 'temporary' : 'persistent'}
          onMenuSelect={isMobile ? handleDrawerClose : undefined}
          focusSearchToken={focusSearchToken}
        />
      )}

      <Main open={open} isMobile={isMobile}>
        {compactMode ? (
          <Box sx={{ height: compactAppBarHeight, flexShrink: 0 }} />
        ) : (
          <DrawerHeader />
        )}
        {/* 셸(헤더·사이드바)은 화면에 고정하고 본문만 스크롤한다.
            Main이 height:100dvh + overflow:hidden 이라 여기서 스크롤 영역을 잡아주지 않으면
            내용이 긴 화면(등록 폼 등)이 잘린 채 스크롤되지 않는다.
            채팅처럼 스스로 높이를 채우는 화면은 넘치지 않으므로 이 스크롤이 생기지 않는다. */}
        <Box
          ref={scrollTargetRef}
          onScroll={compactMode ? handleContentScroll : undefined}
          sx={{
            flexGrow: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            overflowX: 'hidden',
            '&::-webkit-scrollbar': { width: '8px' },
            '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '4px' },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '4px',
              '&:hover': { background: '#a8a8a8' },
            },
          }}
        >
          <Outlet />
        </Box>
      </Main>
    </Box>
  );
};

export default DrawerTemplate;
